import React, { useEffect, useMemo, useState } from "react";
import { FiCalendar, FiFilter, FiRefreshCw, FiSearch } from "react-icons/fi";
import { FaFileExcel, FaSort, FaSortDown, FaSortUp } from "react-icons/fa";
import axiosInstance from "../../libs/axios";
import { ExcelDownload } from "../../utils/excel.utils";
import { Pagination as PaginationUtils, PaginationType } from "../../utils/pagination.utils";
import { DensityToggle, useAttendanceDensity } from "./table-density";
import {
  getAttendanceLeaveTypeLabel,
  getDisplayHRStatusBadgeClasses,
  getDisplayHRStatusLabel,
} from "../../libs/attendance";

type AttendanceScope = "my" | "project-team";
type SortDirection = "ASC" | "DESC";

interface ProjectItem {
  project_id?: number;
  project?: {
    id?: number;
    project_name?: string;
    client_project_id?: number;
    client_project?: {
      id?: number;
      client_project_name?: string;
    } | null;
  } | null;
}

interface AttendanceUser {
  id: number;
  username?: string | null;
  first_name?: string;
  last_name?: string;
  email?: string;
  association_type?: string;
  employee_code?: string | null;
  device_version?: string | null;
  department?: {
    id?: number;
    name?: string;
  } | null;
  state?: {
    id?: number;
    state_name?: string;
    circle_name?: string | null;
  } | null;
  project_manager?: {
    id?: number;
    first_name?: string;
    last_name?: string;
  } | null;
  user_projects?: ProjectItem[] | null;
}

interface AttendanceApprover {
  id: number;
  first_name?: string;
  last_name?: string;
}

interface AttendanceRecord {
  /** null for a "Not Marked" day the server filled in (no row exists yet). */
  id: number | null;
  is_not_marked?: boolean;
  user_id?: number;
  attendance_date: string;
  check_in_time?: string | null;
  check_out_time?: string | null;
  worked_hours?: string | null;
  /** The 23:59 cron closed the day - hours are not real, so show NA. */
  is_auto_checkout?: boolean;
  work_status?: string | null;
  hr_status?: string | null;
  leave_type?: string | null;
  marking_type?: string | null;
  approver?: AttendanceApprover | null;
  user?: AttendanceUser | null;
}

interface AttendanceSortConfig {
  field: string;
  direction: SortDirection;
}

interface AttendanceFilters {
  search: string;
  user_id: string;
  date_from: string;
  date_to: string;
  work_status: string;
  hr_status: string;
  marking_type: string;
}

interface AttendanceRecordsListProps {
  scope: AttendanceScope;
  endpoint: string;
  title: string;
  description: string;
}

const DEFAULT_LIMIT = 25;

const toNumber = (value: unknown, fallback: number): number => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === "string") {
    const parsed = Number(value);
    if (!Number.isNaN(parsed) && Number.isFinite(parsed)) {
      return parsed;
    }
  }

  return fallback;
};

const AttendanceRecordsList: React.FC<AttendanceRecordsListProps> = ({
  scope,
  endpoint,
  title,
  description,
}) => {
  const density = useAttendanceDensity();
  const [records, setRecords] = useState<AttendanceRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(DEFAULT_LIMIT);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [hasPrevPage, setHasPrevPage] = useState(false);
  const [sortConfig, setSortConfig] = useState<AttendanceSortConfig>({
    field: "attendance_date",
    direction: "DESC",
  });
  const [filters, setFilters] = useState<AttendanceFilters>({
    search: "",
    user_id: "",
    date_from: "",
    date_to: "",
    work_status: "",
    hr_status: "",
    marking_type: "",
  });
  const [showExcelModal, setShowExcelModal] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportData, setExportData] = useState<Record<string, any>[]>([]);

  const buildQueryParams = (
    pageValue: number,
    limitValue: number,
  ): Record<string, string | number> => {
    const params: Record<string, string | number> = {
      page: pageValue,
      limit: limitValue,
      sort_by: sortConfig.field,
      sort_order: sortConfig.direction,
    };

    if (scope === "my") {
      // List days that were never marked too, not only days with a row.
      params.include_not_marked = "true";
      if (filters.date_from) params.start_date = filters.date_from;
      if (filters.date_to) params.end_date = filters.date_to;
      return params;
    }

    if (filters.search) params.search = filters.search;
    if (filters.user_id) params.user_id = filters.user_id;
    if (filters.date_from) params.attendance_date_from = filters.date_from;
    if (filters.date_to) params.attendance_date_to = filters.date_to;
    if (filters.work_status) params.work_status = filters.work_status;
    if (filters.hr_status) params.hr_status = filters.hr_status;
    if (filters.marking_type) params.marking_type = filters.marking_type;

    return params;
  };

  const loadRecords = async (): Promise<void> => {
    try {
      setLoading(true);
      const params = buildQueryParams(page, limit);
      const response = await axiosInstance.get(endpoint, { params });
      const responseData = response?.data?.data as
        | {
            attendances?: AttendanceRecord[];
            pagination?: Partial<PaginationType>;
          }
        | undefined;

      // const attendances: any[] = Array.isArray(responseData?.attendances)
      //   ? responseData.attendances ?? []
      //   : [];
      setRecords(responseData?.attendances as any);

      const apiPagination = responseData?.pagination;
      const resolvedTotalCount = toNumber(
        apiPagination?.total_count,
        Number(responseData?.attendances?.length),
      );
      const resolvedTotalPages = toNumber(
        apiPagination?.total_pages,
        Math.max(1, Math.ceil(resolvedTotalCount / limit)),
      );
      const resolvedHasNext =
        typeof apiPagination?.has_next_page === "boolean"
          ? apiPagination.has_next_page
          : page < resolvedTotalPages;
      const resolvedHasPrev =
        typeof apiPagination?.has_prev_page === "boolean"
          ? apiPagination.has_prev_page
          : page > 1;

      setTotalCount(resolvedTotalCount);
      setTotalPages(resolvedTotalPages);
      setHasNextPage(resolvedHasNext);
      setHasPrevPage(resolvedHasPrev);
    } catch (error) {
      console.error("Error loading attendance records:", error);
      setRecords([]);
      setTotalCount(0);
      setTotalPages(0);
      setHasNextPage(false);
      setHasPrevPage(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadRecords();
  }, [endpoint, scope, page, limit, sortConfig, filters]);

  const activeFilterCount = useMemo(() => {
    if (scope === "my") {
      return [filters.date_from, filters.date_to].filter(Boolean).length;
    }

    return [
      filters.search,
      filters.user_id,
      filters.date_from,
      filters.date_to,
      filters.work_status,
      filters.hr_status,
      filters.marking_type,
    ].filter(Boolean).length;
  }, [filters, scope]);

  const pagination: PaginationType = {
    page,
    limit,
    total_count: totalCount,
    total_pages: totalPages,
    has_next_page: hasNextPage,
    has_prev_page: hasPrevPage,
    next_page: hasNextPage ? page + 1 : null,
    prev_page: hasPrevPage ? page - 1 : null,
    offset: Math.max(0, (page - 1) * limit),
  };

  const setFilterValue = (key: keyof AttendanceFilters, value: string): void => {
    setPage(1);
    setFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleResetFilters = (): void => {
    setPage(1);
    setFilters({
      search: "",
      user_id: "",
      date_from: "",
      date_to: "",
      work_status: "",
      hr_status: "",
      marking_type: "",
    });
  };

  const handleSort = (field: string): void => {
    setPage(1);
    setSortConfig((prev) => ({
      field,
      direction: prev.field === field && prev.direction === "ASC" ? "DESC" : "ASC",
    }));
  };

  const getSortIcon = (field: string) => {
    if (sortConfig.field !== field) return <FaSort className="w-3 h-3 text-gray-400" />;
    return sortConfig.direction === "ASC" ? (
      <FaSortUp className="w-3 h-3 text-blue-800" />
    ) : (
      <FaSortDown className="w-3 h-3 text-blue-800" />
    );
  };

  const formatName = (firstName?: string, lastName?: string): string => {
    const fullName = [firstName, lastName].filter(Boolean).join(" ").trim();
    return fullName || "N/A";
  };

  const formatDate = (value?: string | null): string => {
    if (!value) return "N/A";
    return new Date(value).toLocaleDateString();
  };

  const formatTime = (value?: string | null): string => {
    if (!value) return "N/A";
    return new Date(value).toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getDepartmentNames = (user?: AttendanceUser | null): string => {
    const departmentName = user?.department?.name?.trim();
    if (departmentName) {
      return departmentName;
    }

    const projects = user?.user_projects ?? [];
    const names = projects
      .map((item) => item.project?.client_project?.client_project_name)
      .filter((name): name is string => Boolean(name));

    // De-duplicate department names
    const unique = Array.from(new Set(names));
    if (unique.length > 0) {
      return unique.join(", ");
    }

    const projectNames = projects
      .map((item) => item.project?.project_name)
      .filter((name): name is string => Boolean(name));

    return projectNames.length > 0 ? Array.from(new Set(projectNames)).join(", ") : "N/A";
  };

  const getProjectNames = (user?: AttendanceUser | null): string => {
    const projects = user?.user_projects ?? [];
    const names = projects
      .map((item) => item.project?.project_name)
      .filter((name): name is string => Boolean(name));

    const unique = Array.from(new Set(names));
    return unique.length > 0 ? unique.join(", ") : "N/A";
  };

  const mapRecordForExport = (record: AttendanceRecord): Record<string, any> => ({
    "Attendance Date": formatDate(record.attendance_date),
    Employee: formatName(record.user?.first_name, record.user?.last_name),
    Username: record.user?.username || "N/A",
    "Employee Code": record.user?.employee_code || "N/A",
    Circle: record.user?.state?.circle_name || record.user?.state?.state_name || "N/A",
    "Department/Project Group": getDepartmentNames(record.user),
    "Project Names": getProjectNames(record.user),
    "App Version": record.user?.device_version || "N/A",
    "Reporting Person": formatName(
      record.user?.project_manager?.first_name,
      record.user?.project_manager?.last_name,
    ),
    "Check In": formatTime(record.check_in_time),
    "Check Out": formatTime(record.check_out_time),
    "Worked Hours": record.is_auto_checkout ? "NA" : record.worked_hours || "N/A",
    "Work Status": record.work_status === "leave" ? "Leave Applied" : record.work_status || "N/A",
    "Leave Type": record.work_status === "leave" ? getAttendanceLeaveTypeLabel(record.leave_type) : "",
    "HR Status": record.hr_status ? getDisplayHRStatusLabel(record.hr_status, record.work_status) : "N/A",
    "Marking Type": record.marking_type || "N/A",
    Approver: formatName(record.approver?.first_name, record.approver?.last_name),
  });

  const handleExport = async (): Promise<void> => {
    try {
      setExporting(true);

      const exportLimit = 50;
      let exportPage = 1;
      let exportTotalPages = 1;
      const allRecords: AttendanceRecord[] = [];

      do {
        const response = await axiosInstance.get(endpoint, {
          params: buildQueryParams(exportPage, exportLimit),
        });

        const responseData = response?.data?.data as
          | {
              attendances?: AttendanceRecord[];
              pagination?: Partial<PaginationType>;
            }
          | undefined;

        const rows = Array.isArray(responseData?.attendances)
          ? responseData?.attendances ?? []
          : [];
        allRecords.push(...rows);

        const paginationInfo = responseData?.pagination;
        exportTotalPages = toNumber(
          paginationInfo?.total_pages,
          rows.length < exportLimit ? exportPage : exportTotalPages,
        );

        exportPage += 1;
      } while (exportPage <= exportTotalPages);

      setExportData(allRecords.map(mapRecordForExport));
      setShowExcelModal(true);
    } catch (error) {
      console.error("Error exporting attendance records:", error);
      setExportData([]);
    } finally {
      setExporting(false);
    }
  };

  const getWorkStatusBadge = (status?: string | null, leaveType?: string | null) => {
    const normalized = status ?? "pending";
    const styles: Record<string, string> = {
      worked: "bg-green-100 text-green-800",
      idle: "bg-yellow-100 text-yellow-800",
      absent: "bg-red-100 text-red-800",
      leave: "bg-blue-100 text-blue-900",
      weekly_off: "bg-purple-100 text-purple-700",
      holiday: "bg-indigo-100 text-indigo-700",
      pending: "bg-gray-100 text-gray-700",
      not_marked: "bg-orange-100 text-orange-800",
    };

    const cls = styles[normalized] ?? styles.pending;
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${cls}`}>
        {normalized === "leave"
          ? `Leave Applied${leaveType ? ` (${getAttendanceLeaveTypeLabel(leaveType)})` : ""}`
          : normalized === "pending"
            ? "Pending Approval"
            : normalized === "not_marked"
              ? "Not Marked"
              : normalized.replace("_", " ")}
      </span>
    );
  };

  const tableColumnsCount = scope === "project-team" ? 13 : 8;

  return (
    <div className="space-y-6">
      <div className="bg-white rounded shadow-sm border border-gray-200 p-4">
        <h1 className="text-xl font-semibold text-gray-900">{title}</h1>
        <p className="text-sm text-gray-600 mt-1">{description}</p>
      </div>

      <div className="bg-white rounded shadow-sm border border-gray-200 p-4 space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
            <FiFilter className="w-4 h-4" />
            Filters
            {activeFilterCount > 0 && (
              <span className="bg-blue-800 text-white text-xs px-2 py-0.5 rounded-full">
                {activeFilterCount}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <DensityToggle density={density} />
            {scope === "project-team" && (
              <button
                type="button"
                onClick={() => void handleExport()}
                disabled={exporting}
                className="px-3 py-2 text-sm text-white bg-green-600 border border-green-600 rounded hover:bg-green-700 transition-colors flex items-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <FaFileExcel className="w-4 h-4" />
                {exporting ? "Preparing..." : "Export"}
              </button>
            )}
            <button
              type="button"
              onClick={handleResetFilters}
              className="px-3 py-2 text-sm text-gray-700 border border-gray-300 rounded hover:bg-gray-50 transition-colors flex items-center gap-2"
            >
              <FiRefreshCw className="w-4 h-4" />
              Reset
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {scope === "project-team" && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
              <div className="relative">
                <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  value={filters.search}
                  onChange={(event) => setFilterValue("search", event.target.value)}
                  className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-800 focus:border-blue-800"
                  placeholder="Name, email, or code"
                />
              </div>
            </div>
          )}

          {scope === "project-team" && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">User ID</label>
              <input
                type="number"
                min={1}
                value={filters.user_id}
                onChange={(event) => setFilterValue("user_id", event.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-800 focus:border-blue-800"
                placeholder="Enter user id"
              />
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {scope === "my" ? "Start Date" : "Attendance From"}
            </label>
            <input
              type="date"
              value={filters.date_from}
              onChange={(event) => setFilterValue("date_from", event.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-800 focus:border-blue-800"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {scope === "my" ? "End Date" : "Attendance To"}
            </label>
            <input
              type="date"
              value={filters.date_to}
              onChange={(event) => setFilterValue("date_to", event.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-800 focus:border-blue-800"
            />
          </div>

          {scope === "project-team" && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Work Status</label>
                <select
                  value={filters.work_status}
                  onChange={(event) => setFilterValue("work_status", event.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-800 focus:border-blue-800"
                >
                  <option value="">All</option>
                  <option value="worked">Worked</option>
                  <option value="idle">Idle</option>
                  <option value="absent">Absent</option>
                  <option value="leave">Leave Applied</option>
                  <option value="weekly_off">Weekly Off</option>
                  <option value="holiday">Holiday</option>
                  <option value="pending">Pending Approval</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">HR Status</label>
                <select
                  value={filters.hr_status}
                  onChange={(event) => setFilterValue("hr_status", event.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-800 focus:border-blue-800"
                >
                  <option value="">All</option>
                  <option value="present">Present</option>
                  <option value="absent">Absent</option>
                  <option value="pending">Pending Approval</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Marking Type</label>
                <select
                  value={filters.marking_type}
                  onChange={(event) => setFilterValue("marking_type", event.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-800 focus:border-blue-800"
                >
                  <option value="">All</option>
                  <option value="auto">Auto</option>
                  <option value="manual">Manual</option>
                </select>
              </div>
            </>
          )}
        </div>
      </div>

      <div className="bg-white rounded shadow-sm border border-gray-200">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className={`${density.headCell} text-left font-medium text-gray-600 uppercase`}>
                  <button
                    type="button"
                    onClick={() => handleSort("attendance_date")}
                    className={`flex items-center gap-2 text-gray-600 hover:text-blue-800 uppercase font-semibold ${density.isCompact ? "text-[10px]" : "text-xs"}`}
                  >
                    Date
                    {getSortIcon("attendance_date")}
                  </button>
                </th>
                {scope === "project-team" && (
                  <>
                    <th className={`${density.headCell} text-left font-medium text-gray-600 uppercase`}>Employee</th>
                    <th className={`${density.headCell} text-left font-medium text-gray-600 uppercase`}>Employee Code</th>
                    <th className={`${density.headCell} text-left font-medium text-gray-600 uppercase`}>Circle</th>
                    <th className={`${density.headCell} text-left font-medium text-gray-600 uppercase`}>Department</th>
                    <th className={`${density.headCell} text-left font-medium text-gray-600 uppercase`}>Reporting Person</th>
                  </>
                )}
                <th className={`${density.headCell} text-left font-medium text-gray-600 uppercase`}>
                  <button
                    type="button"
                    onClick={() => handleSort("check_in_time")}
                    className={`flex items-center gap-2 text-gray-600 hover:text-blue-800 uppercase font-semibold ${density.isCompact ? "text-[10px]" : "text-xs"}`}
                  >
                    Check In
                    {getSortIcon("check_in_time")}
                  </button>
                </th>
                <th className={`${density.headCell} text-left font-medium text-gray-600 uppercase`}>
                  <button
                    type="button"
                    onClick={() => handleSort("check_out_time")}
                    className={`flex items-center gap-2 text-gray-600 hover:text-blue-800 uppercase font-semibold ${density.isCompact ? "text-[10px]" : "text-xs"}`}
                  >
                    Check Out
                    {getSortIcon("check_out_time")}
                  </button>
                </th>
                <th className={`${density.headCell} text-left font-medium text-gray-600 uppercase`}>
                  <button
                    type="button"
                    onClick={() => handleSort("worked_hours")}
                    className={`flex items-center gap-2 text-gray-600 hover:text-blue-800 uppercase font-semibold ${density.isCompact ? "text-[10px]" : "text-xs"}`}
                  >
                    Hours
                    {getSortIcon("worked_hours")}
                  </button>
                </th>
                <th className={`${density.headCell} text-left font-medium text-gray-600 uppercase`}>
                  <button
                    type="button"
                    onClick={() => handleSort("work_status")}
                    className={`flex items-center gap-2 text-gray-600 hover:text-blue-800 uppercase font-semibold ${density.isCompact ? "text-[10px]" : "text-xs"}`}
                  >
                    Work Status
                    {getSortIcon("work_status")}
                  </button>
                </th>
                <th className={`${density.headCell} text-left font-medium text-gray-600 uppercase`}>HR Status</th>
                <th className={`${density.headCell} text-left font-medium text-gray-600 uppercase`}>Type</th>
                <th className={`${density.headCell} text-left font-medium text-gray-600 uppercase`}>Approver</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan={tableColumnsCount} className={`${density.placeholderCell} text-center`}>
                    <div className="flex flex-col items-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-800 mb-3"></div>
                      <span className="text-sm text-gray-600">Loading attendance records...</span>
                    </div>
                  </td>
                </tr>
              ) : records.length === 0 ? (
                <tr>
                  <td colSpan={tableColumnsCount} className={`${density.placeholderCell} text-center`}>
                    <FiCalendar className="w-10 h-10 text-gray-400 mx-auto mb-3" />
                    <p className="text-sm text-gray-500">No attendance records found</p>
                  </td>
                </tr>
              ) : (
                records.map((record) => (
                  <tr key={record.id ?? `not-marked-${record.attendance_date}`} className={record.is_not_marked ? "bg-orange-50/40 hover:bg-orange-50" : "hover:bg-gray-50"}>
                    <td className={`${density.bodyCell} whitespace-nowrap`}>
                      {new Date(record.attendance_date).toLocaleDateString()}
                    </td>
                    {scope === "project-team" && (
                      <>
                        <td className={`${density.bodyCell} whitespace-nowrap font-medium text-gray-900`}>
                          {formatName(record.user?.first_name, record.user?.last_name)}
                        </td>
                        <td className={`${density.bodyCell} whitespace-nowrap`}>
                          {record.user?.employee_code || "N/A"}
                        </td>
                        <td className={`${density.bodyCell} whitespace-nowrap`}>
                          {record.user?.state?.circle_name || record.user?.state?.state_name || "N/A"}
                        </td>
                        <td style={{ minWidth: density.px(220) }} className={density.bodyCell}>{getDepartmentNames(record.user)}</td>
                        <td className={`${density.bodyCell} whitespace-nowrap`}>
                          {formatName(record.user?.project_manager?.first_name, record.user?.project_manager?.last_name)}
                        </td>
                      </>
                    )}
                    <td className={`${density.bodyCell} whitespace-nowrap`}>{formatTime(record.check_in_time)}</td>
                    <td className={`${density.bodyCell} whitespace-nowrap`}>{formatTime(record.check_out_time)}</td>
                    <td className={`${density.bodyCell} whitespace-nowrap`} title={record.is_auto_checkout ? "Auto checkout - the user did not check out" : undefined}>{record.is_auto_checkout ? "NA" : record.worked_hours || "N/A"}</td>
                    <td className={`${density.bodyCell} whitespace-nowrap`}>{getWorkStatusBadge(record.work_status, record.leave_type)}</td>
                    <td className={`${density.bodyCell} whitespace-nowrap`}>
                      {record.is_not_marked ? (
                        <span className="px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">Not Marked</span>
                      ) : record.hr_status ? (
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getDisplayHRStatusBadgeClasses(record.hr_status, record.work_status)}`}>
                          {getDisplayHRStatusLabel(record.hr_status, record.work_status)}
                        </span>
                      ) : "N/A"}
                    </td>
                    <td className={`${density.bodyCell} whitespace-nowrap capitalize`}>
                      {record.marking_type || "N/A"}
                    </td>
                    <td className={`${density.bodyCell} whitespace-nowrap`}>
                      {formatName(record.approver?.first_name, record.approver?.last_name)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <PaginationUtils
          pagination={pagination}
          onPageChange={(nextPage) => setPage(nextPage)}
          onLimitChange={(nextLimit) => {
            setLimit(nextLimit);
            setPage(1);
          }}
        />
      </div>

      <ExcelDownload
        isOpen={showExcelModal}
        onClose={() => setShowExcelModal(false)}
        data={exportData}
        filename={scope === "project-team" ? "all_team_attendance" : "attendance_records"}
        title="Export Attendance Data"
        description="Download the filtered attendance records with all available details"
      />
    </div>
  );
};

export default AttendanceRecordsList;
